import {
  useTrustpilotAverageRating,
  useTrustpilotReviewCount,
  useTrustpilotReviewsList,
  type TrustpilotDisplayData,
} from "@made-people/grease-trustpilot";
import { useTranslate } from "@made-people/grease-translations";
import { useReviewsLoadMore } from "../use-reviews-load-more";
import { StarRating, ReviewCard } from "./trustpilot-shared";
import { useLocale } from "../../../../hooks/use-locale";
import { TRUSTPILOT_CONSTANTS } from "../trustpilot-constants";
import { useMemo } from "react";

export const TrustpilotDrawerContent = ({
  type,
  productSku,
  productReviewsData,
}: {
  type: "product" | "store";
  productSku?: string;
  productReviewsData?: TrustpilotDisplayData | null;
}) => {
  const translate = useTranslate();
  const locale = useLocale();

  const storeAverageRating = useTrustpilotAverageRating("store");
  const storeTotalReviews = useTrustpilotReviewCount("store");
  const storeReviews = useTrustpilotReviewsList("store");

  const averageRating =
    type === "product"
      ? (productReviewsData?.averageRating ?? 0)
      : storeAverageRating;
  const totalReviews =
    type === "product"
      ? (productReviewsData?.totalReviews ?? 0)
      : storeTotalReviews;
  const allReviews =
    type === "product" ? (productReviewsData?.reviews ?? []) : storeReviews;

  const initialReviews = useMemo(() => {
    return allReviews.slice(0, TRUSTPILOT_CONSTANTS.REVIEWS_PER_PAGE_INITIAL);
  }, [allReviews]);

  const availableReviews = allReviews;

  const { reviews, loadMore, canLoadMore, isLoading } = useReviewsLoadMore(
    initialReviews,
    type,
    productSku,
    totalReviews,
    availableReviews,
  );

  if (initialReviews.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-white">
        <p className="text-lg">{translate("Trustpilot_No_Reviews")}</p>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <div className="flex flex-col items-center mb-8">
        <div className="text-5xl lg:text-6xl mb-2">
          {averageRating.toFixed(1)}
        </div>
        <p className="text-md lg:text-lg text-center mb-3">
          {totalReviews.toLocaleString(locale)}{" "}
          {translate("Trustpilot_Reviews")}
        </p>
        <StarRating rating={averageRating} starClassName="h-6 w-6" />
      </div>

      <div className="flex flex-col gap-4 mb-8">
        {reviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>

      {canLoadMore && (
        <button
          onClick={loadMore}
          disabled={isLoading}
          className="border border-black bg-black text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full py-2 flex items-center justify-center gap-2"
        >
          {isLoading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {isLoading
            ? translate("Trustpilot_Loading")
            : translate("Trustpilot_Load_More")}
        </button>
      )}

      {!canLoadMore && reviews.length > 12 && (
        <p className="text-center">{translate("Trustpilot_All_Loaded")}</p>
      )}
    </div>
  );
};
